import { $convertFromMarkdownString } from '@lexical/markdown';
import { $createTextNode, $getRoot, $isTextNode } from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { Box, Button, keyframes, styled } from '@mui/material';
import * as Sentry from '@sentry/react';
import { css } from '@emotion/react';
import { FC, useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';

import { getLexicalEditorBaseConfig } from '../../../components/LexicalEditor/LexicalEditor';
import { ChatFollowBottomContext } from '../hooks/useFollowBottom';
import { useSmoothStream } from '../hooks/useSmoothStream';
import { parseMessageContent, resolveHideLinkPath } from '../utils/parseMessageContent';
import { CHATBOT_TRANSFORMERS } from '../utils/markdownTransformers';
import { splitSettledTail, stripUnterminatedMarkers } from '../utils/streamingMarkdown';
import { FeedbackButtons, FeedbackProps } from './FeedbackButtons';
import {
  ChatBubblePaddingY,
  ChatEmphasisColor,
  ChatEmphasisFontWeight,
  ChatListItemGap,
  ChatParagraphGap,
  ChatProseFontFamily,
  ChatProseFontSize,
  ChatProseLineHeight,
} from './style-defs';

const viewResultsButtonCss = css`
  margin-top: 12px;
  display: block;
  width: fit-content;
`;

const BotMessageWrapper = styled(Box)`
  min-width: 100px;
  padding: ${({ theme }) => theme.spacing(ChatBubblePaddingY, 4)};
  background: ${({ theme }) => theme.palette.background.default};
  color: ${({ theme }) => theme.palette.text.secondary};
  font-family: ${ChatProseFontFamily};
  font-size: ${ChatProseFontSize};
  line-height: ${ChatProseLineHeight};

  /* GlobalStyles stamps Wotfard-Regular on every element via a universal rule,
     and a direct declaration beats inheritance — Lexical's nested p/span/li
     would never see the wrapper's face without this reset. */
  * {
    font-family: inherit;
  }

  code,
  pre,
  .editor-code,
  .editor-text-code {
    font-family: Menlo, Consolas, Monaco, monospace;
  }

  /* Emphasis matches the table headers below: body face at weight 600,
     near-black, so bold reads the same in prose and tables and pops against
     the secondary-gray prose. */
  strong,
  b,
  .editor-text-bold {
    font-weight: ${ChatEmphasisFontWeight};
    color: ${ChatEmphasisColor};
  }

  .editor-paragraph {
    margin-bottom: ${ChatParagraphGap};
  }

  .editor-paragraph:last-child {
    margin-bottom: 0;
  }

  /* Lexical's note-editor stylesheet gives list items 8px top/bottom margins
     and a 48px indent (.editor-listitem + .editor-list-ul); chat wants
     Slack-tight bullets with a modest hanging indent. */
  ul,
  ol {
    margin: 4px 0;
    padding-left: 1.75em;
  }

  li {
    margin: 0 0 ${ChatListItemGap};
  }

  li:last-child {
    margin-bottom: 0;
  }

  /* One chat-scale heading style — the playground's gray uppercase h2 and
     browser-default h3 otherwise leak in. Air above a section label, tight
     to the list beneath it. */
  h1,
  h2,
  h3,
  h4 {
    margin: 12px 0 4px;
    font-weight: ${ChatEmphasisFontWeight};
    line-height: 1.3;
    color: ${ChatEmphasisColor};
    text-transform: none;
  }

  h1 {
    font-size: 17px;
  }

  h2,
  h3,
  h4 {
    font-size: 15px;
  }

  /* Table styling to match AG Grid aesthetic */
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
    font-size: 14px;
    border: 1px solid #d2d5db;
  }

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    border: 1px solid #d2d5db;
    min-height: 42px;
  }

  th {
    background-color: #f8f8fb;
    font-weight: ${ChatEmphasisFontWeight};
    color: ${ChatEmphasisColor};
    font-size: 14px;
  }

  tbody tr {
    background-color: #ffffff;
  }

  tbody tr:nth-of-type(odd) {
    background-color: #fbfcfd;
  }

  tbody tr:hover {
    background-color: #f8f8fb;
  }
`;

interface BotMessageProps {
  content: string;
  animate?: boolean;
  feedbackProps?: FeedbackProps;
}

const sentryNamespace = 'fd-assistant';

const tailFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const TailWrapper = styled(Box)`
  animation: ${tailFadeIn} 250ms ease-out;
`;

export const BotMessage: FC<BotMessageProps> = ({ content, animate = false, feedbackProps }) => {
  const navigate = useNavigate();
  // Streams reveal at a steady time-paced cadence; history and completed
  // replies render in full. The list follows each slice once Lexical has
  // committed it to the DOM (see MarkdownBlock's onRendered).
  const displayedContent = useSmoothStream(content, animate);
  const followBottom = useContext(ChatFollowBottomContext);

  const { cleanContent, hideLinks } = useMemo(
    () => parseMessageContent(displayedContent),
    [displayedContent]
  );
  // The reply always renders as its settled blocks plus the open tail block,
  // so completing (or cancelling) a stream is an in-place update rather than
  // a remount that would blank the bubble for a frame. While streaming, closed
  // blocks stay put and only the tail re-parses per revealed slice —
  // rebuilding the whole Lexical tree every frame is what made long replies
  // stutter.
  const { settled, tail } = useMemo(() => splitSettledTail(cleanContent), [cleanContent]);
  const onRendered = animate ? followBottom : undefined;
  // Each newly opened block mounts fresh and fades in (keyed on the boundary)
  // while streaming; the key then freezes so the final flush and completion
  // update the tail editor in place instead of remounting it.
  const tailKeyRef = useRef(0);
  if (animate) {
    tailKeyRef.current = settled.length;
  }

  return (
    <BotMessageWrapper>
      {/* No per-message icon or byline (FD-4912) — the panel header carries the
          branding, and the live activity line renders at the bottom of the
          message list. */}
      <Box hidden={settled === ''}>
        <MarkdownBlock content={settled} streaming={animate} onRendered={onRendered} />
      </Box>
      <TailWrapper key={tailKeyRef.current} hidden={tail === ''}>
        <MarkdownBlock
          content={animate ? stripUnterminatedMarkers(tail) : tail}
          marginTop={settled !== '' ? ChatParagraphGap : '0'}
          streaming={animate}
          onRendered={onRendered}
        />
      </TailWrapper>
      {!animate &&
        hideLinks.map((link) => (
          <Button
            key={link.path}
            variant='contained'
            size='small'
            css={viewResultsButtonCss}
            onClick={() => navigate(resolveHideLinkPath(link.path))}
          >
            {link.label}
          </Button>
        ))}
      {feedbackProps && <FeedbackButtons {...feedbackProps} />}
    </BotMessageWrapper>
  );
};

interface MarkdownBlockProps {
  content: string;
  marginTop?: string;
  /** The block is still receiving streamed slices (enables the append fast path). */
  streaming?: boolean;
  /** Fires after Lexical has committed `content` to the DOM. */
  onRendered?: () => void;
}

/** A read-only Lexical render of one markdown string. */
function MarkdownBlock({ content, marginTop = '0', streaming = false, onRendered }: MarkdownBlockProps) {
  return (
    <LexicalComposer
      initialConfig={{
        ...getLexicalEditorBaseConfig(),
        namespace: sentryNamespace,
        editable: false,
        onError: (error) => {
          Sentry.captureException(error, { tags: { feature: sentryNamespace } });
        },
      }}
    >
      <RichTextPlugin
        contentEditable={<ContentEditable style={{ wordBreak: 'break-word', marginTop }} />}
        placeholder={() => <span />}
        ErrorBoundary={LexicalErrorBoundary}
      />
      <UpdateTextPlugin content={content} streaming={streaming} onRendered={onRendered} />
    </LexicalComposer>
  );
}

interface UpdateTextPluginProps {
  content: string;
  streaming?: boolean;
  onRendered?: () => void;
}

/** Any character that can open or close markdown syntax. A suffix free of
 *  these is plain prose and can be appended to the last text node in place. */
const MarkdownSyntaxChars = /[*_`[\]()|#>~<\n\\]/;
/** Parents whose text must not be extended by a plain append — the appended
 *  prose would silently become part of the link or code. */
const NonAppendableParents = new Set(['link', 'autolink', 'code']);
/** A line holding only a block marker (`-`, `1.`, `##`, `>`): the next word
 *  turns it into a list item/heading/quote, which needs a full re-parse — an
 *  in-place append would leave it as literal `- item` text. */
const BareBlockMarker = /^\s*(?:[-+*]|\d+\.|#{1,6}|>)$/;

export function UpdateTextPlugin({ content, streaming = false, onRendered }: UpdateTextPluginProps) {
  const [editor] = useLexicalComposerContext();
  const renderedRef = useRef<string | null>(null);
  const wasStreamingRef = useRef(streaming);

  useEffect(() => {
    const previous = renderedRef.current;
    // The end of a stream always gets one canonical full parse: it collapses
    // the appended word nodes and corrects anything the fast path's
    // heuristics let through, even when the text itself didn't change.
    const finishedStreaming = wasStreamingRef.current && !streaming;
    wasStreamingRef.current = streaming;
    if (previous === content && !finishedStreaming) {
      return;
    }
    renderedRef.current = content;
    const suffix =
      !finishedStreaming && previous !== null && content.startsWith(previous)
        ? content.slice(previous.length)
        : null;
    const lastLine = previous === null ? '' : previous.slice(previous.lastIndexOf('\n') + 1);
    const canAppend =
      suffix !== null &&
      suffix !== '' &&
      !MarkdownSyntaxChars.test(suffix) &&
      !BareBlockMarker.test(lastLine);

    // Lexical commits asynchronously; onUpdate runs once the DOM reflects it,
    // which is the earliest point a follow-scroll sees the new height.
    editor.update(
      () => {
        // Streaming fast path: plain prose arriving after an unformatted
        // trailing text node is inserted as its own token-mode text node with
        // a fade-in, so each released word softly appears while the rest of
        // the block's DOM stays put. Token mode keeps Lexical from merging the
        // words back into one run (which would drop the animation); the
        // editor is read-only, so the mode has no other effect. Rebuilding
        // the block from markdown every slice replaced every node in it.
        if (canAppend) {
          const last = $getRoot().getLastDescendant();
          const parentType = last?.getParent()?.getType();
          if (
            $isTextNode(last) &&
            last.getFormat() === 0 &&
            parentType !== undefined &&
            !NonAppendableParents.has(parentType) &&
            last.getTextContent().length > 0 &&
            previous !== null &&
            previous.endsWith(last.getTextContent())
          ) {
            const fresh = $createTextNode(suffix);
            fresh.setMode('token');
            fresh.setStyle(`animation: ${tailFadeIn.name} 240ms ease-out`);
            last.insertAfter(fresh);
            return;
          }
        }
        $convertFromMarkdownString(content, CHATBOT_TRANSFORMERS);
      },
      { onUpdate: onRendered }
    );
  }, [content, streaming, editor, onRendered]);

  return null;
}
